import React, { useState, useEffect, useMemo } from "react";
import axios from "../axios";
import { Menu, MenuButton, MenuItems, MenuItem } from "@headlessui/react";
import {
    format,
    startOfMonth,
    endOfDay,
    endOfMonth,
    setMonth,
    eachDayOfInterval,
    parse,
} from "date-fns";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
} from "recharts";
import { sl } from "date-fns/locale";
import { useWidth } from "../hooks/useWidth";

axios.defaults.withCredentials = true;

export default function FilterChartSystem() {
    const { isLower } = useWidth(640);
    const [selectedTimeFrame, setSelectedTimeFrame] = useState("po_mesecih");
    const [selectedAgent, setSelectedAgent] = useState("SKUPAJ");
    const [currentData, setCurrentData] = useState([]);
    const [currentSum, setCurrentSum] = useState(0);
    const [currentAvg, setCurrentAvg] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [dateRange, setDateRange] = useState([null, null]);
    const [startDate, endDate] = dateRange;
    const [includePreInvoices, setIncludePreInvoices] = useState(false);

    const timeFilters = [
        // Months
        {
            label: "Po mesecih",
            value: "po_mesecih",
            getDate: () => {
                const now = new Date();
                const year = now.getFullYear();
                const start = startOfMonth(new Date(year, 0)); // January 1st
                const end = endOfMonth(new Date(year, 11)); // December 31st
                return [start, end];
            },
        },
        {
            label: "Januar",
            value: "jan",
            getDate: () => {
                const now = new Date();
                const start = startOfMonth(setMonth(now, 0));
                const end = endOfMonth(start);
                return [start, end];
            },
        },
        {
            label: "Februar",
            value: "feb",
            getDate: () => {
                const now = new Date();
                const start = startOfMonth(setMonth(now, 1));
                const end = endOfMonth(start);
                return [start, end];
            },
        },
        {
            label: "Marec",
            value: "mar",
            getDate: () => {
                const now = new Date();
                const start = startOfMonth(setMonth(now, 2));
                const end = endOfMonth(start);
                return [start, end];
            },
        },
        {
            label: "April",
            value: "apr",
            getDate: () => {
                const now = new Date();
                const start = startOfMonth(setMonth(now, 3));
                const end = endOfMonth(start);
                return [start, end];
            },
        },
        {
            label: "Maj",
            value: "maj",
            getDate: () => {
                const now = new Date();
                const start = startOfMonth(setMonth(now, 4));
                const end = endOfMonth(start);
                return [start, end];
            },
        },
        {
            label: "Junij",
            value: "jun",
            getDate: () => {
                const now = new Date();
                const start = startOfMonth(setMonth(now, 5));
                const end = endOfMonth(start);
                return [start, end];
            },
        },
        {
            label: "Julij",
            value: "jul",
            getDate: () => {
                const now = new Date();
                const start = startOfMonth(setMonth(now, 6));
                const end = endOfMonth(start);
                return [start, end];
            },
        },
        {
            label: "Avgust",
            value: "avg",
            getDate: () => {
                const now = new Date();
                const start = startOfMonth(setMonth(now, 7));
                const end = endOfMonth(start);
                return [start, end];
            },
        },
        {
            label: "September",
            value: "sep",
            getDate: () => {
                const now = new Date();
                const start = startOfMonth(setMonth(now, 8));
                const end = endOfMonth(start);
                return [start, end];
            },
        },
        {
            label: "Oktober",
            value: "okt",
            getDate: () => {
                const now = new Date();
                const start = startOfMonth(setMonth(now, 9));
                const end = endOfMonth(start);
                return [start, end];
            },
        },
        {
            label: "November",
            value: "nov",
            getDate: () => {
                const now = new Date();
                const start = startOfMonth(setMonth(now, 10));
                const end = endOfMonth(start);
                return [start, end];
            },
        },
        {
            label: "December",
            value: "dec",
            getDate: () => {
                const now = new Date();
                const start = startOfMonth(setMonth(now, 11));
                const end = endOfMonth(start);
                return [start, end];
            },
        },

        { label: "Po meri", value: "custom", getDate: () => dateRange },
    ];

    const agents = [
        { label: "SKUPAJ", value: "SKUPAJ" },
        { label: "EČ", value: "EČ" },
        { label: "AP", value: "AP" },
        { label: "SĐ", value: "SĐ" },
        { label: "ND", value: "ND" },
        { label: "NMR", value: "NMR" },
        { label: "RL", value: "RL" },
        { label: "JC", value: "JC" },
        { label: "MJ", value: "MJ" },
        { label: "UT", value: "UT" },
        { label: "JD", value: "JD" },
        { label: "TD", value: "TD" },
        { label: "KK", value: "KK" },
        { label: "ZP", value: "ZP" },
        { label: "RLM", value: "RLM" },
        { label: "STO", value: "STO" },
    ];

    const handleTimeFrameSelect = (filter) => {
        setSelectedTimeFrame(filter.value);
        if (filter.value === "custom") {
            setShowDatePicker(true);
        } else {
            const [start, end] = filter.getDate();
            setDateRange([start, end]);
            setShowDatePicker(false);
            setIncludePreInvoices(false);
        }
    };

    const fetchData = async (start, end, agent, includePreInvoices) => {
        setLoading(true);
        setError(null);

        try {
            const params = {
                startDate: format(start, "yyyy-MM-dd"),
                endDate: format(endOfDay(end), "yyyy-MM-dd"),
                agent: agent === "SKUPAJ" ? "ALL" : agent,
                includePreInvoices,
            };

            const response = await axios.get("/api/promet", { params });
            return response.data;
        } catch (err) {
            console.error("Error fetching data:", err);
            const errorMessage =
                err.response?.data?.error ||
                err.message ||
                "Napaka pri nalaganju podatkov";
            setError(errorMessage);
            return { data: [], sum: 0, avg: 0 };
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const loadData = async () => {
            let start, end;

            if (selectedTimeFrame === "custom") {
                if (!dateRange[0] || !dateRange[1]) return;
                [start, end] = dateRange;
            } else {
                const timeFilter = timeFilters.find(
                    (f) => f.value === selectedTimeFrame
                );
                if (!timeFilter) return;
                [start, end] = timeFilter.getDate();
            }

            const result = await fetchData(
                start,
                end,
                selectedAgent,
                includePreInvoices
            );

            // Step 1: Group original data by date string formatted with date-fns
            const groupedByDay = result.data.reduce((acc, { date, value }) => {
                const formattedDate = format(new Date(date), "d. M. yyyy", {
                    locale: sl,
                });

                if (!acc[formattedDate]) {
                    acc[formattedDate] = 0;
                }
                acc[formattedDate] += value;
                return acc;
            }, {});

            // Step 2: Generate all dates between start and end
            const allDates = eachDayOfInterval({
                start: new Date(start),
                end: new Date(end),
            });

            // Step 3: Format and fill chart data
            const chartData = allDates.map((date) => {
                const formattedDate = format(date, "d. M. yyyy", {
                    locale: sl,
                });
                return {
                    date: formattedDate,
                    value: groupedByDay[formattedDate] || 0,
                };
            });

            if (selectedTimeFrame === "po_mesecih") {
                const groupedByMonth = result.data.reduce(
                    (acc, { date, value }) => {
                        // Parse date string like "1/7/2025" (MM/DD/YYYY or D/M/YYYY)
                        let monthKey = format(date, "MMM", {
                            locale: sl,
                        }); // e.g. "0 = jan, 1 = feb,..."

                        // * For some reason we get "ma" from backend for "maj"
                        // * console.log({ monthKey });
                        if (monthKey.toLowerCase() !== "maj") {
                            monthKey = monthKey.slice(0, -1);
                        }

                        if (!acc[monthKey]) {
                            acc[monthKey] = 0;
                        }

                        acc[monthKey] += value;
                        return acc;
                    },
                    {}
                );

                // Ensure all 12 months are in the result
                const allMonths = [
                    "Jan",
                    "Feb",
                    "Mar",
                    "Apr",
                    "Maj",
                    "Jun",
                    "Jul",
                    "Avg",
                    "Sep",
                    "Okt",
                    "Nov",
                    "Dec",
                ];

                const chartData = allMonths.map((month) => ({
                    date: month,
                    value: groupedByMonth[month.toLowerCase()] || 0,
                }));

                setCurrentData(chartData || []);
                setCurrentSum(result.sum || 0);
                setCurrentAvg(result.avg || 0);
                return;
            }

            setCurrentData(chartData || []);
            setCurrentSum(result.sum || 0);
            setCurrentAvg(result.avg || 0);
        };

        loadData();
    }, [dateRange, selectedAgent, selectedTimeFrame, includePreInvoices]);

    const getSelectedTimeFrameLabel = () => {
        const filter = timeFilters.find((t) => t.value === selectedTimeFrame);
        if (filter.value === "custom" && startDate && endDate) {
            return `${format(startDate, "dd.MM.yyyy")} - ${format(
                endDate,
                "dd.MM.yyyy"
            )}`;
        }
        return filter ? filter.label.toUpperCase() : "NEZNANO OBDOBJE";
    };

    const currentTotalRevenue = useMemo(() => currentSum, [currentSum]);

    if (loading) {
        return (
            <div className="flex items-center justify-center p-8">
                <div className="w-8 h-8 border-b-2 border-blue-500 rounded-full animate-spin"></div>
                <span className="ml-2">Nalaganje podatkov...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-4 mb-8 border border-red-200 rounded-lg bg-red-50">
                <p className="text-red-600">{error}</p>
                <button
                    onClick={async () => {
                        setError(null);
                        const result = await fetchData(
                            start,
                            end,
                            selectedAgent,
                            false
                        );

                        // Step 1: Group original data by date string formatted with date-fns
                        const groupedByDay = result.data.reduce(
                            (acc, { date, value }) => {
                                const formattedDate = format(
                                    new Date(date),
                                    "d. M. yyyy",
                                    {
                                        locale: sl,
                                    }
                                );

                                if (!acc[formattedDate]) {
                                    acc[formattedDate] = 0;
                                }
                                acc[formattedDate] += value;
                                return acc;
                            },
                            {}
                        );

                        // Step 2: Generate all dates between start and end
                        const allDates = eachDayOfInterval({
                            start: new Date(start),
                            end: new Date(end),
                        });

                        // Step 3: Format and fill chart data
                        const chartData = allDates.map((date) => {
                            const formattedDate = format(date, "d. M. yyyy", {
                                locale: sl,
                            });
                            return {
                                date: formattedDate,
                                value: groupedByDay[formattedDate] || 0,
                            };
                        });

                        if (selectedTimeFrame === "po_mesecih") {
                            const groupedByMonth = result.data.reduce(
                                (acc, { date, value }) => {
                                    // Parse date string like "1/7/2025" (MM/DD/YYYY or D/M/YYYY)
                                    let monthKey = format(date, "MMM", {
                                        locale: sl,
                                    }); // e.g. "0 = jan, 1 = feb,..."

                                    // * For some reason we get "ma" from backend for "maj"
                                    // * console.log({ monthKey });
                                    if (monthKey.toLowerCase() !== "maj") {
                                        monthKey = monthKey.slice(0, -1);
                                    }

                                    if (!acc[monthKey]) {
                                        acc[monthKey] = 0;
                                    }

                                    acc[monthKey] += value;
                                    return acc;
                                },
                                {}
                            );

                            // Ensure all 12 months are in the result
                            const allMonths = [
                                "Jan",
                                "Feb",
                                "Mar",
                                "Apr",
                                "Maj",
                                "Jun",
                                "Jul",
                                "Avg",
                                "Sep",
                                "Okt",
                                "Nov",
                                "Dec",
                            ];

                            const chartData = allMonths.map((month) => ({
                                date: month,
                                value: groupedByMonth[month.toLowerCase()] || 0,
                            }));

                            setCurrentData(chartData || []);
                            setCurrentSum(result.sum || 0);
                            setCurrentAvg(result.avg || 0);
                            return;
                        }

                        setCurrentData(chartData || []);
                        setCurrentSum(result.sum || 0);
                        setCurrentAvg(result.avg || 0);
                    }}
                    className="mt-2 text-sm text-red-600 hover:text-red-800"
                >
                    Poskusi ponovno
                </button>
            </div>
        );
    }

    return (
        <div className="container px-4 mx-auto">
            {/* Header - Made more responsive and modern */}
            <div className="mb-8 text-center">
                <h1 className="mb-2 text-xl font-bold sm:text-2xl lg:text-3xl">
                    Dobrodošli v Stoja analitiko
                </h1>
                <span className="text-2xl font-extrabold text-red-600 sm:text-3xl lg:text-4xl">
                    STOJALITIKA
                </span>
            </div>

            {/* Filter Section - Improved layout and spacing */}
            <div className="p-4 mb-6 bg-white rounded-lg shadow-md">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                    <div className="space-y-2">
                        <label className="block font-semibold text-gray-700">
                            Izberi časovno obdobje:
                        </label>
                        <Menu as="div" className="relative">
                            <MenuButton className="flex items-center justify-between w-full px-3 py-2 text-sm text-left bg-white border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                                <span>{getSelectedTimeFrameLabel()}</span>
                                <svg
                                    className="w-5 h-5 text-gray-400"
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                >
                                    <path
                                        fillRule="evenodd"
                                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                        clipRule="evenodd"
                                    />
                                </svg>
                            </MenuButton>
                            <MenuItems className="absolute z-10 w-full mt-1 overflow-y-auto scrolling-touch bg-white border rounded-lg shadow-lg focus:outline-none max-h-60 sm:max-h-none">
                                <div className="py-1">
                                    {timeFilters.map((filter) => (
                                        <MenuItem key={filter.value}>
                                            {({ active }) => (
                                                <button
                                                    className={`${
                                                        active
                                                            ? "bg-blue-50 text-blue-700"
                                                            : "text-gray-700"
                                                    } ${
                                                        selectedTimeFrame ===
                                                        filter.value
                                                            ? "bg-blue-50 font-medium"
                                                            : ""
                                                    } w-full px-4 py-2 text-left text-sm hover:bg-blue-50 transition-colors duration-150`}
                                                    onClick={() =>
                                                        handleTimeFrameSelect(
                                                            filter
                                                        )
                                                    }
                                                >
                                                    {filter.label}
                                                </button>
                                            )}
                                        </MenuItem>
                                    ))}
                                </div>
                            </MenuItems>
                        </Menu>
                        {showDatePicker && (
                            <div className="absolute z-20 p-4 mt-1 -translate-x-1/2 bg-white border rounded-lg shadow-lg left-1/2">
                                <DatePicker
                                    selectsRange={true}
                                    startDate={startDate}
                                    endDate={endDate}
                                    onChange={(update) => {
                                        setDateRange(update);
                                        if (update[0] && update[1]) {
                                            setIncludePreInvoices(false);
                                            setShowDatePicker(false);
                                        }
                                    }}
                                    isClearable={true}
                                    inline
                                    className="react-datepicker"
                                    calendarClassName="custom-calendar"
                                    dayClassName={(date) =>
                                        date >= startDate && date <= endDate
                                            ? "bg-blue-50 text-blue-700"
                                            : undefined
                                    }
                                />
                                <div className="flex justify-end mt-4 space-x-2">
                                    <button
                                        onClick={() => setShowDatePicker(false)}
                                        className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
                                    >
                                        Prekliči
                                    </button>
                                    <button
                                        onClick={() => {
                                            if (startDate && endDate) {
                                                setShowDatePicker(false);
                                            }
                                        }}
                                        className="px-3 py-1 text-sm text-white bg-blue-500 rounded hover:bg-blue-600"
                                        disabled={!startDate || !endDate}
                                    >
                                        Potrdi
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="space-y-2">
                        <label className="block font-semibold text-gray-700">
                            Izberi agenta:
                        </label>
                        <select
                            value={selectedAgent}
                            onChange={(e) => {
                                setSelectedAgent(e.target.value);
                                setIncludePreInvoices(false);
                            }}
                            className="w-full px-3 py-2 text-sm bg-white border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            {agents.map((agent) => (
                                <option key={agent.value} value={agent.value}>
                                    {agent.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* Stats Cards - New section */}
            <div className="grid grid-cols-1 gap-6 mb-6 md:grid-cols-2 lg:grid-cols-3">
                <div className="p-6 bg-white rounded-lg shadow-md">
                    <div className="flex flex-col items-center justify-between mb-2 sm:flex-row">
                        <h3 className="text-lg font-semibold text-gray-700">
                            Skupni promet
                        </h3>
                        <button
                            type="button"
                            onClick={() =>
                                setIncludePreInvoices(!includePreInvoices)
                            }
                            className={`px-2 py-1 font-medium text-sm transition-colors ${
                                includePreInvoices
                                    ? "bg-green-500 text-white hover:bg-green-600"
                                    : "bg-gray-200 text-gray-800 hover:bg-gray-300"
                            }`}
                        >
                            {includePreInvoices
                                ? "Izklopi predračune"
                                : "Vklopi predračune"}
                        </button>
                    </div>
                    <p className="text-3xl font-bold text-blue-600">
                        €{currentTotalRevenue.toLocaleString()}
                    </p>
                    <p className="mt-2 text-sm text-gray-500">
                        {getSelectedTimeFrameLabel()}
                    </p>
                </div>

                <div className="p-6 bg-white rounded-lg shadow-md">
                    <h3 className="mb-2 text-lg font-semibold text-gray-700">
                        Dnevno povprečje
                    </h3>
                    <p className="text-3xl font-bold text-green-600">
                        €{currentAvg.toFixed(2)}
                    </p>
                    <p className="mt-2 text-sm text-gray-500">
                        Glede na izbrano časovno obdobje
                    </p>
                </div>

                <div className="p-6 bg-white rounded-lg shadow-md sm:col-span-2 lg:col-span-1">
                    <h3 className="mb-2 text-lg font-semibold text-gray-700">
                        Agent
                    </h3>
                    <p className="text-3xl font-bold text-purple-600">
                        {selectedAgent}
                    </p>
                    <p className="mt-2 text-sm text-gray-500">
                        Trenutno izbran agent
                    </p>
                </div>
            </div>

            {/* Chart Section - Improved responsiveness */}
            <div className="p-4 bg-white rounded-lg shadow-md sm:p-6">
                <div className="flex flex-col gap-4 mb-6 sm:flex-row sm:justify-start sm:items-center">
                    <h3 className="mb-2 text-xl font-semibold text-gray-700 sm:mb-0">
                        Promet za obdobje
                    </h3>
                    <h3 className="text-sm text-gray-500">
                        {getSelectedTimeFrameLabel()}
                    </h3>
                </div>

                <div className="h-[300px] sm:h-[400px] lg:h-[500px] sm:mx-0 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                            data={currentData}
                            margin={{ top: 10, right: 10, left: 10, bottom: 0 }}
                        >
                            <CartesianGrid
                                strokeDasharray="3 3"
                                stroke="#E5E7EB"
                            />
                            <XAxis
                                dataKey="date"
                                tick={{ fontSize: 12, fill: "#4B5563" }}
                                tickFormatter={(value) => {
                                    return isLower ? "" : value;
                                }}
                                angle={-45}
                                textAnchor="end"
                                height={60}
                                stroke="#9CA3AF"
                                interval="preserveStartEnd"
                                minTickGap={15}
                            />
                            <YAxis
                                tick={{ fontSize: 12, fill: "#4B5563" }}
                                tickFormatter={(value) => {
                                    return isLower ? "" : `€${value}`;
                                }}
                                width={isLower ? 0 : 70}
                                stroke="#9CA3AF"
                            />
                            <Tooltip
                                contentStyle={{
                                    backgroundColor:
                                        "rgba(255, 255, 255, 0.95)",
                                    border: "none",
                                    borderRadius: "0.5rem",
                                    boxShadow:
                                        "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                                    padding: "0.75rem",
                                }}
                                formatter={(value) => [
                                    `€${value.toLocaleString()}`,
                                    "Prihodek",
                                ]}
                                labelFormatter={(label) => label}
                                cursor={{ fill: "rgba(37, 99, 235, 0.1)" }}
                                wrapperStyle={{
                                    outline: "none",
                                    touchAction: "pan-x pan-y",
                                }}
                            />
                            <Bar
                                dataKey="value"
                                fill="#2563EB"
                                radius={[4, 4, 0, 0]}
                                barSize={32}
                            />
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
}
